import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFeaturedProfiles } from '../services/api';
import { Button } from '../components/ui/button';
import { ArrowRight, MapPin, Zap, Clock, Filter, Brain, CheckCircle2 } from 'lucide-react';

// Tag color palette
const TAG_COLORS = [
  { bg: '#DBEAFE', text: '#1E40AF' },
  { bg: '#D1FAE5', text: '#065F46' },
  { bg: '#FEE2E2', text: '#991B1B' },
  { bg: '#E0E7FF', text: '#3730A3' },
  { bg: '#FCE7F3', text: '#9D174D' },
  { bg: '#FEF3C7', text: '#92400E' },
  { bg: '#CFFAFE', text: '#155E75' },
  { bg: '#F3E8FF', text: '#6B21A8' },
];

const getTagColor = (tag) => {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
};

// Profile Card Component - compact style
function ProfileCard({ profile }) {
  return (
    <Link 
      to={`/p/${profile.slug}`}
      className="block bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
      style={{ width: '320px', flexShrink: 0 }}
    >
      <div className="p-6">
        <div className="flex gap-4 mb-4">
          <div 
            className="w-16 h-20 rounded-xl bg-[#F2F0ED] flex items-center justify-center text-2xl flex-shrink-0 overflow-hidden"
          >
            {profile.photo_url ? (
              <img src={profile.photo_url} alt={profile.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl">👤</span>
            )}
          </div>
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-medium text-[#1A1918] truncate">{profile.name}</h3>
            <p className="text-sm text-[#666460] font-medium truncate">{profile.role}</p>
            {profile.location && (
              <p className="text-xs text-[#999] flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" />
                {profile.location}
              </p>
            )}
          </div>
        </div>

        {profile.badge && (
          <div className="mb-3">
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#A855F7] bg-[#FAF5FF] px-2 py-1 rounded-full">
              <span>✦</span> {profile.badge}
            </span>
          </div>
        )}

        {profile.tags && profile.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {profile.tags.slice(0, 3).map((tag, i) => {
              const color = getTagColor(tag);
              return (
                <span
                  key={i}
                  className="text-xs font-medium px-2 py-0.5 rounded-full"
                  style={{ background: color.bg, color: color.text }}
                >
                  {tag}
                </span>
              );
            })}
          </div>
        )}

        {profile.stats && profile.stats.length > 0 && (
          <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#E6E4E0]">
            {profile.stats.slice(0, 3).map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-sm font-bold text-[#1A1918]">{stat.value}</p>
                <p className="text-xs text-[#999]">{stat.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function LandingPage() {
  const { isAuthenticated, isAdmin } = useAuth();
  const [featuredProfiles, setFeaturedProfiles] = useState([]);

  useEffect(() => {
    const loadProfiles = async () => {
      try {
        const data = await getFeaturedProfiles(3);
        setFeaturedProfiles(data.profiles || []);
      } catch (error) {
        console.error('Failed to load featured profiles:', error);
      }
    };
    loadProfiles();
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FAF9F7]/80 backdrop-blur-md border-b border-[#E6E4E0]">
        <div className="container-app flex items-center justify-between h-16">
          <Link to="/" className="font-heading text-2xl font-medium text-[#1A1918]">
            brief
          </Link>
          <nav className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <Link to="/admin">
                    <Button variant="ghost" className="btn-ghost" data-testid="admin-btn">
                      Admin
                    </Button>
                  </Link>
                )}
                <Link to="/editor">
                  <Button className="btn-primary h-10 px-6" data-testid="my-page-btn">
                    My Page
                  </Button>
                </Link>
              </>
            ) : (
              <Link to="/login">
                <Button variant="outline" className="btn-secondary h-10 px-6" data-testid="login-btn">
                  Login
                </Button>
              </Link>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="container-app text-center">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium text-[#1A1918] mb-6 animate-slide-up max-w-4xl mx-auto" data-testid="hero-title">
            Find your next unicorn.<br />
            <span className="text-[#A855F7]">Before everyone else.</span>
          </h1>
          
          <p className="text-lg md:text-xl text-[#666460] max-w-2xl mx-auto mb-10 animate-slide-up" style={{ animationDelay: '0.1s' }}>
            Brief is the investor profile that attracts quality deal flow, filters noise with AI, and helps you close the founders you want.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <Link to="/waitlist">
              <Button className="btn-primary flex items-center gap-2" data-testid="create-page-btn">
                Create your Brief
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/p/vijen-patel">
              <Button variant="outline" className="btn-secondary" data-testid="see-examples-btn">
                See examples
              </Button>
            </Link>
          </div>

          {/* Key Stats - Minimal */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-12 gap-y-4 animate-slide-up" style={{ animationDelay: '0.25s' }}>
            <div className="text-center">
              <div className="text-2xl font-bold text-[#1A1918]">10x</div>
              <div className="text-sm text-[#666460]">Better inbound quality</div>
            </div>
            <div className="w-px h-10 bg-[#E6E4E0] hidden sm:block" />
            <div className="text-center">
              <div className="text-2xl font-bold text-[#1A1918]">5 min</div>
              <div className="text-sm text-[#666460]">To create your page</div>
            </div>
            <div className="w-px h-10 bg-[#E6E4E0] hidden sm:block" />
            <div className="text-center">
              <div className="text-2xl font-bold text-[#1A1918]">0</div>
              <div className="text-sm text-[#666460]">Cold emails to read</div>
            </div>
          </div>

          {/* Featured Profile Cards */}
          <div className="mt-24 animate-slide-up" style={{ animationDelay: '0.35s' }}>
            {featuredProfiles.length > 0 ? (
              <div className="relative overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#FAF9F7] to-transparent z-10 pointer-events-none" />
                <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#FAF9F7] to-transparent z-10 pointer-events-none" />
                <div className="flex gap-6 justify-center pb-4 px-4 overflow-x-auto scrollbar-hide md:overflow-visible">
                  {featuredProfiles.map((profile, i) => (
                    <ProfileCard key={profile.slug || i} profile={profile} />
                  ))}
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#FAF9F7] to-transparent pointer-events-none" />
              </div>
            ) : (
              <div className="max-w-3xl mx-auto">
                <div className="preview-window">
                  <div className="preview-bar">
                    <div className="preview-dot"></div>
                    <div className="preview-dot"></div>
                    <div className="preview-dot"></div>
                  </div>
                  <div className="p-8 flex gap-6">
                    <div className="w-24 h-24 rounded-2xl bg-[#F2F0ED] flex items-center justify-center text-4xl">
                      💼
                    </div>
                    <div className="text-left">
                      <h3 className="font-heading text-2xl font-medium text-[#1A1918]">Sarah Chen</h3>
                      <p className="text-[#666460] font-medium mb-2">Partner @ Sequoia Capital</p>
                      <p className="text-sm text-[#999] mb-3">Series A · $5M-$15M checks · AI Infrastructure</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] rounded-full text-xs font-medium">Series A</span>
                        <span className="px-3 py-1 bg-[#D1FAE5] text-[#065F46] rounded-full text-xs font-medium">AI/ML</span>
                        <span className="px-3 py-1 bg-[#F3E8FF] text-[#6B21A8] rounded-full text-xs font-medium">Infrastructure</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-[#1A1918] mb-8">
              You&apos;re drowning in noise.<br />Missing the signal.
            </h2>
            
            <p className="text-lg text-[#666460] leading-relaxed mb-12">
              The best founders don&apos;t pitch everyone — they pitch investors they can understand in 30 seconds. If your thesis is vague, your check size unclear, or your value-add buried, you won&apos;t get the first call.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
              <div className="p-6 rounded-xl bg-[#FAF9F7]">
                <Clock className="w-6 h-6 text-[#A855F7] mb-4" />
                <div className="text-xl font-bold text-[#1A1918] mb-1">20+ hrs/week</div>
                <p className="text-[#666460] text-sm">Wasted on unqualified inbound</p>
              </div>
              <div className="p-6 rounded-xl bg-[#FAF9F7]">
                <Filter className="w-6 h-6 text-[#A855F7] mb-4" />
                <div className="text-xl font-bold text-[#1A1918] mb-1">73%</div>
                <p className="text-[#666460] text-sm">Of pitches don&apos;t match your thesis</p>
              </div>
              <div className="p-6 rounded-xl bg-[#FAF9F7]">
                <Zap className="w-6 h-6 text-[#A855F7] mb-4" />
                <div className="text-xl font-bold text-[#1A1918] mb-1">3 in 10</div>
                <p className="text-[#666460] text-sm">Great founders never reach out</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution - Key Features */}
      <section className="py-20 md:py-32" id="features">
        <div className="container-app">
          <div className="section-header">
            <p className="section-label">The solution</p>
            <h2 className="section-title">Your 24/7 deal flow machine</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Feature 1 - One Link */}
            <div className="card-base">
              <ArrowRight className="w-6 h-6 text-[#A855F7] mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1918] mb-2">One Link. Everywhere.</h3>
              <p className="text-[#666460] text-sm mb-4">
                Twitter, LinkedIn, email signature — one link that always works.
              </p>
              <ul className="space-y-2">
                {["Instant calendar booking", "Custom pitch forms", "Update once, live everywhere"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#666460]">
                    <CheckCircle2 className="w-4 h-4 text-[#A855F7] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Feature 2 - AI Scoring */}
            <div className="card-base">
              <Brain className="w-6 h-6 text-[#A855F7] mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1918] mb-2">AI-Powered Pitch Scoring</h3>
              <p className="text-[#666460] text-sm mb-4">
                Every pitch scored 0-100 based on your criteria — industry, stage, founder strength.
              </p>
              <ul className="space-y-2">
                {["Custom criteria you control", "Founder-market fit analysis", "Instant summaries"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#666460]">
                    <CheckCircle2 className="w-4 h-4 text-[#A855F7] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Feature 3 - Smart Inbox */}
            <div className="card-base">
              <Filter className="w-6 h-6 text-[#A855F7] mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1918] mb-2">Smart Pitch Inbox</h3>
              <p className="text-[#666460] text-sm mb-4">
                All pitches in one place, pre-sorted by AI. See decks and scores at a glance.
              </p>
              <ul className="space-y-2">
                {["Auto-ranked by fit", "One-click deck viewing", "Star, archive, respond fast"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#666460]">
                    <CheckCircle2 className="w-4 h-4 text-[#A855F7] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Feature 4 - Attract Founders */}
            <div className="card-base">
              <Zap className="w-6 h-6 text-[#A855F7] mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1918] mb-2">Attract Better Founders</h3>
              <p className="text-[#666460] text-sm mb-4">
                Top founders research investors first. Your Brief tells them why you&apos;re the right partner.
              </p>
              <ul className="space-y-2">
                {["Clear thesis = qualified inbound", "Showcase portfolio wins", "Build trust before meeting"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#666460]">
                    <CheckCircle2 className="w-4 h-4 text-[#A855F7] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-32 bg-white" id="how">
        <div className="container-app">
          <div className="section-header">
            <p className="section-label">How it works</p>
            <h2 className="section-title">Three steps. Five minutes.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                num: "1",
                title: "Import your LinkedIn",
                desc: "Upload your PDF or paste a URL. Brief extracts your experience and builds your page automatically."
              },
              {
                num: "2",
                title: "Set your criteria",
                desc: "Define your thesis, stage preferences, and AI criteria. Tell Brief what makes a great pitch for you."
              },
              {
                num: "3",
                title: "Publish and receive",
                desc: "Share your Brief and start receiving pre-qualified, AI-scored pitches directly to your inbox."
              }
            ].map((step, i) => (
              <div key={i} className="card-base" data-testid={`step-${i + 1}`}>
                <div className="w-10 h-10 rounded-full bg-[#1A1918] text-white flex items-center justify-center font-medium mb-4">
                  {step.num}
                </div>
                <h3 className="text-xl font-medium text-[#1A1918] mb-2">{step.title}</h3>
                <p className="text-[#666460]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's On Your Page */}
      <section className="py-20 md:py-32" id="whats-included">
        <div className="container-app">
          <div className="section-header">
            <p className="section-label">What&apos;s on your page</p>
            <h2 className="section-title">Everything founders need to pitch you right.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { emoji: "💰", title: "Investment thesis", desc: "Stage, check size, ownership targets — crystal clear" },
              { emoji: "🎯", title: "Focus areas", desc: "Sectors and business models you care about" },
              { emoji: "🚀", title: "Pitch submission", desc: "Custom forms that capture exactly what you need" },
              { emoji: "🤖", title: "AI screening", desc: "Every pitch scored and summarized automatically" },
              { emoji: "📅", title: "Calendar booking", desc: "Let qualified founders book time directly" },
              { emoji: "🔗", title: "One link", desc: "Newsletter, socials, portfolio — all in one place" }
            ].map((item, i) => (
              <div key={i} className="p-5 rounded-xl bg-[#FAF9F7] hover:bg-[#F2F0ED] transition-colors">
                <div className="text-2xl mb-3">{item.emoji}</div>
                <h4 className="font-medium text-[#1A1918] mb-1">{item.title}</h4>
                <p className="text-sm text-[#666460]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#1A1918]">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-white mb-4">
            Stop wasting time on bad pitches.
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Create your Brief in 5 minutes. Get higher-quality inbound from day one.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/waitlist">
              <Button className="bg-white text-[#1A1918] hover:bg-white/90 h-12 px-8 rounded-full font-medium" data-testid="cta-create-btn">
                Create your Brief — Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/p/vijen-patel">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-8 rounded-full font-medium">
                See examples
              </Button>
            </Link>
          </div>
          
          {/* Trust indicators */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-white/50 text-sm">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> No credit card required
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Setup in 5 minutes
            </span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[#E6E4E0]">
        <div className="container-app flex items-center justify-between">
          <span className="font-heading text-lg font-medium text-[#1A1918]">brief</span>
          <span className="text-sm text-[#666460]">© 2024 Brief. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
